import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Alert,
  LinearProgress,
  Typography,
  Box,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

const PASSWORD_RULES = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "One number", test: (p) => /\d/.test(p) },
  {
    label: "One special character",
    test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p),
  },
];

const CreateUserDialog = ({
  open,
  onClose,
  onSave,
  employee = null,
  employees = [],
  roles = [],
  loading = false,
}) => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [mail, setMail] = useState("");
  const [tempPassword, setTempPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      if (employee) {
        setSelectedEmployee(employee);
        setMail(employee.businessEmail || "");
      } else {
        setSelectedEmployee(null);
        setMail("");
      }
      setTempPassword("");
      setConfirmPassword("");
      setSelectedRoles([]);
      setError("");
    }
  }, [open, employee]);

  const handleEmployeeChange = (_, value) => {
    setSelectedEmployee(value);
    setMail(value?.businessEmail || "");
  };

  const passedRules = PASSWORD_RULES.filter((r) => r.test(tempPassword));
  const passwordStrength = (passedRules.length / PASSWORD_RULES.length) * 100;
  const passwordValid = passedRules.length === PASSWORD_RULES.length;

  const availableEmployees = employees.filter(
    (e) => e.status === 1 && !e.hasUser
  );

  const handleSubmit = () => {
    if (!selectedEmployee) return setError("Please select an employee");
    if (!mail?.trim()) return setError("Business email is required");
    if (!passwordValid) return setError("Password does not meet requirements");
    if (tempPassword !== confirmPassword)
      return setError("Passwords do not match");
    if (selectedRoles.length === 0)
      return setError("Please select at least one role");

    onSave({
      employeeId: selectedEmployee.employeeId,
      mail: mail.trim(),
      tempPassword,
      roleIds: selectedRoles,
    });
  };

  const displayName = selectedEmployee
    ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
    : "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Create User Account{displayName ? ` — ${displayName}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {!employee && (
            <Grid size={12}>
              <Autocomplete
                options={availableEmployees}
                value={selectedEmployee}
                onChange={handleEmployeeChange}
                getOptionLabel={(opt) =>
                  `${opt.firstName} ${opt.lastName} — ${opt.businessEmail || "—"}`
                }
                isOptionEqualToValue={(opt, val) =>
                  opt.employeeId === val.employeeId
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Employee *"
                    size="small"
                    helperText="Only active employees without a user account are shown"
                  />
                )}
                size="small"
              />
            </Grid>
          )}

          <Grid size={12}>
            <TextField
              label="Business Email (login) *"
              value={mail}
              onChange={(e) => setMail(e.target.value)}
              fullWidth
              size="small"
              type="email"
              helperText="This will be the user's login email. Only use business email."
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Temporary Password *"
              value={tempPassword}
              onChange={(e) => setTempPassword(e.target.value)}
              fullWidth
              size="small"
              type="password"
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Confirm Password *"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              size="small"
              type="password"
              error={
                confirmPassword.length > 0 && tempPassword !== confirmPassword
              }
              helperText={
                confirmPassword.length > 0 && tempPassword !== confirmPassword
                  ? "Passwords do not match"
                  : ""
              }
            />
          </Grid>
          {tempPassword && (
            <Grid size={12}>
              <LinearProgress
                variant="determinate"
                value={passwordStrength}
                color={
                  passwordStrength < 50
                    ? "error"
                    : passwordStrength < 100
                    ? "warning"
                    : "success"
                }
                sx={{ mb: 1, borderRadius: 1 }}
              />
              {PASSWORD_RULES.map((rule) => (
                <Box
                  key={rule.label}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  {rule.test(tempPassword) ? (
                    <CheckCircleIcon
                      sx={{ fontSize: 14, color: "success.main" }}
                    />
                  ) : (
                    <CancelIcon sx={{ fontSize: 14, color: "error.main" }} />
                  )}
                  <Typography
                    variant="caption"
                    color={
                      rule.test(tempPassword)
                        ? "success.main"
                        : "text.secondary"
                    }
                  >
                    {rule.label}
                  </Typography>
                </Box>
              ))}
            </Grid>
          )}

          <Grid size={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Roles *</InputLabel>
              <Select
                multiple
                value={selectedRoles}
                onChange={(e) => setSelectedRoles(e.target.value)}
                label="Roles *"
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((roleId) => {
                      const role = roles.find((r) => r.id === roleId);
                      return (
                        <Chip
                          key={roleId}
                          label={role?.name || roleId}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? "Creating..." : "Create User"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUserDialog;
